/**
 * Sección «Estados Financieros»: el paquete completo en una sola página.
 *
 * Pide el paquete UNA vez y lo reparte a todas las pantallas por contexto.
 * Aquí viven el selector de período, la generación del paquete y la barra de
 * hojas, que son del cierre entero y no de ninguna hoja en particular.
 *
 * Las once hojas son pestañas y no once entradas del menú porque el entregable
 * es UN documento: el contador no consulta «el Anexo ESF», consulta el paquete
 * y se mueve entre sus hojas, igual que en el Excel del que salió.
 */

import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { api } from "../../api/client";
import { useAsync } from "../../api/useAsync";
import type { Config, SessionInfo } from "../../api/types";
import { PeriodPicker } from "../../components/PeriodPicker";
import { defaultPeriod } from "../../components/period";
import { ErrorState, Loading } from "../../components/States";
import { mayReadJournal } from "./closingContext";
import type { ClosingContext } from "./closingContext";
import { ExportMenu } from "../../components/ExportMenu";
import { Tabs } from "../../components/Tabs";
import { closingTabs, sheetForPath, subViewForPath } from "./tabs";

export function ClosingLayout({
  config,
  session,
}: {
  config: Config;
  session: SessionInfo;
}) {
  const [period, setPeriod] = useState(defaultPeriod);
  const [support, setSupport] = useState(false);
  const { data, error, loading } = useAsync(
    () => api.closing(period),
    [period.periodFrom, period.periodTo],
  );

  // Sin distintivo por pestaña: ver `shared.tsx`. `closingTabs` sigue aceptando
  // uno para la pestaña que algún día lo necesite de verdad.
  const tabs = closingTabs(config.closing.sheets);

  // La hoja abierta se descarga SOLA, con la misma presentación que lleva
  // dentro del paquete. El endpoint existía desde el principio; lo que faltaba
  // era ofrecerlo: el contador que necesita mandar solo el Estado de Flujos no
  // tiene por qué generar las once hojas y recortar.
  const { pathname } = useLocation();
  const hoja = sheetForPath(config.closing.sheets, pathname);
  const ficha = hoja && config.reports.find((report) => report.key === hoja.key);
  const exportable = Boolean(ficha);
  // La auditoría por niveles solo aplica donde el reporte declara árbol.
  const arbol = ficha?.tree ? ficha : undefined;
  // El detalle por cuenta y la conciliación mensual son vistas de la hoja, no
  // pantallas aparte: su descarga sale de este mismo menú cuando están abiertas.
  const subvista = subViewForPath(pathname);
  const detalle =
    subvista && config.reports.find((report) => report.key === subvista.reportKey);

  return (
    /* `cierre` marca las once hojas del paquete como REPORTE FORMAL. Ahí la
       cifra negativa se dice solo con el paréntesis, sin rojo: es la notación
       del documento, el PDF se imprime en negro, y un estado firmado con cifras
       de colores se lee como una alerta y no como un estado. El rojo se queda
       donde es una ayuda de trabajo —el Balance de Comprobación, los libros, la
       auditoría—, que es pantalla y no entregable.

       El gris del cero SÍ se queda: no afirma nada, solo baja el contraste de lo
       que no aporta, y el PDF hace lo mismo con su propio peso tipográfico. */
    <div className="cierre">
      {/* UNA franja de estado, no dos banners y tres distintivos. */}

      {/* UN control de exportación, no tres. La hoja abierta, el paquete
          entero y el árbol de auditoría salían de tres sitios distintos de la
          misma pantalla, ninguno de los cuales decía en qué se diferenciaba de
          los otros. */}
      <PeriodPicker value={period} onChange={setPeriod}>
        <ExportMenu
          documento={
            exportable && hoja
              ? {
                  label: `Hoja oficial · ${hoja.seq} ${hoja.sheet}`,
                  note: "La hoja abierta, tal como va dentro del paquete y como se firma.",
                  xlsx: api.downloadUrl(hoja.key, "xlsx", period),
                  pdf: api.downloadUrl(hoja.key, "pdf", period),
                }
              : undefined
          }
          paquete={{
            label: "Paquete de cierre completo",
            note: "Las once hojas en su orden, con portada y firmas.",
            xlsx: api.closingPackageUrl("xlsx", period, support),
            pdf: api.closingPackageUrl("pdf", period, support),
            extra: (
              <label className="check">
                <input
                  type="checkbox"
                  checked={support}
                  onChange={(event) => setSupport(event.target.checked)}
                />
                Incluir Balance de Comprobación
              </label>
            ),
          }}
          detalle={
            detalle && subvista
              ? {
                  label: `${subvista.label} · ${detalle.title}`,
                  note: "La tabla que está viendo, con todas sus filas.",
                  xlsx: api.downloadUrl(detalle.key, "xlsx", period),
                  pdf: api.downloadUrl(detalle.key, "pdf", period),
                }
              : undefined
          }
          auditoria={
            arbol
              ? {
                  reportKey: arbol.key,
                  period,
                  levels: arbol.tree?.export_levels ?? [],
                  mayReadJournal: mayReadJournal(session),
                }
              : undefined
          }
        />
      </PeriodPicker>

      <Tabs tabs={tabs} label="Hojas del paquete de cierre" />

      {loading && <Loading what="el paquete de cierre" />}
      {error && (
        <ErrorState
          error={error}
          hint="Si aún no hay una corrida publicada, sincronice primero desde Corridas."
        />
      )}
      {data && (
        <Outlet
          context={{ package: data, config, session, period } satisfies ClosingContext}
        />
      )}
    </div>
  );
}
